import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'antd';

import DisplayDuration from '@src/components/DisplayDuration';
import EllipsisWithTooltip from '@src/components/EllipsisWithTooltip/EllipsisWithTooltip';
import TableProgress from '@src/components/Table/components/TableProgress';
import { getUserTimeZoneDate } from '@src/packages/utils';
import { getDurationFromStartDate } from '@src/packages/utils/functions/schedule';
import IntlMessages from '@src/util/IntlMessages';

import { useScheduleDashboardContext } from '../context/ScheduleDashboardContext';
import styles from '../styles.module.less';

const useSchedulesDashboardColumns = () => {
  const profile = useSelector(({ user }) => user.profile);
  const { locale = 'es' } = useSelector(({ settings }) => settings.locale);

  const { groupBy, goToTimeline, setSelectedUsers, toggleDrawer } = useScheduleDashboardContext();

  const intl = useIntl();

  return [
    {
      key: 'schedule',
      title: intl.formatMessage({ id: 'schedules.list.title' }),
      dataIndex: 'schedule',
      align: 'left',
      ellipsis: true,
      width: 150,
      render: record => {
        return <EllipsisWithTooltip text={record.schedule?.name || record.schedule?.id || '-'} />;
      },
    },
    {
      key: 'division',
      title: intl.formatMessage({ id: 'schedules.list.division.label' }),
      dataIndex: 'schedule',
      align: 'left',
      ellipsis: true,
      width: 160,
      render: record => <EllipsisWithTooltip text={record.schedule?.divisionName || '-'} />,
    },
    {
      key: 'duration',
      title: intl.formatMessage({ id: 'schedules.list.duration.label' }),
      dataIndex: 'schedule',
      align: 'center',
      render: record => <DisplayDuration minutes={record?.schedule?.duration || 0} />,
    },
    {
      key: 'duration_time',
      title: intl.formatMessage({ id: `schedules.list.time.${locale}.label` }),
      dataIndex: 'duration',
      align: 'center',
      render: record => {
        const { schedule, duration } = record.schedule || {};
        const { startAt, timeZone: scheduleTimeZone } = schedule || {};

        if (!duration || !startAt) return '-';
        const toMillis = ts => (ts ? ts._seconds * 1000 + ts._nanoseconds / 1_000_000 : null);

        const startAtMillis = toMillis(startAt);
        const dateWithTimeZone = getUserTimeZoneDate(
          startAtMillis,
          scheduleTimeZone,
          profile?.data?.timeZone,
        ).jsDate;

        return (
          <IntlMessages
            id={`schedules.list.time.${locale}.value`}
            values={{
              startDate: dateWithTimeZone,
              endDate: getDurationFromStartDate(dateWithTimeZone, duration),
            }}
          />
        );
      },
    },
    {
      key: 'members',
      title: intl.formatMessage({ id: 'schedules.list.users.label' }),
      dataIndex: 'schedule',
      align: 'center',
      render: record => {
        const members = record.schedule?.members || {};
        const users = members.users || [];
        const groups = members.groups || [];
        const totalUsers = users.length + groups.length;

        if (totalUsers === 0) return '-';

        if (totalUsers === 1) {
          const singleItem = users[0] || groups[0];
          return `${singleItem.firstName || ''} ${singleItem.lastName || ''}`.trim();
        }

        return (
          <Button
            type="link"
            className="gx-mb-0 gx-px-0"
            onClick={() => {
              // Add your users drawer logic here
              setSelectedUsers(members);
              toggleDrawer('users');
            }}
          >
            {totalUsers} {intl.formatMessage({ id: 'general.users' })}
          </Button>
        );
      },
    },
    {
      key: 'events',
      title: intl.formatMessage({ id: 'schedules.list.total.events' }),
      dataIndex: 'aggregation',
      align: 'center',
      render: record => {
        const { finishedExecutions, expectedExecutions } = record.scheduleData;
        return (
          <Button
            type="link"
            className="gx-mb-0 gx-px-0"
            disabled={finishedExecutions === 0}
            onClick={() =>
              finishedExecutions > 0 &&
              goToTimeline({
                divisionId: record.schedule.divId,
                scheduleId: record.schedule.id,
                triggerID: null,
                dateKeyArg: record.dateKey,
                groupByArg: groupBy,
                finishedExecutions,
                expectedExecutions,
              })
            }
          >
            {finishedExecutions}/{expectedExecutions}
          </Button>
        );
      },
    },
    {
      key: 'progress',
      title: intl.formatMessage({ id: 'schedules.list.progress.label' }),
      dataIndex: 'scheduleData',
      align: 'center',
      render: record => (
        <div className={styles.progressContainer} style={{ minWidth: '120px' }}>
          <TableProgress percentage={record.scheduleData.finishedPercentage} />
        </div>
      ),
    },
  ];
};

export default useSchedulesDashboardColumns;
